// Package imports:
import { useEffect, useState } from 'react';
// Third-party libraries:
import cx from 'classnames';
import { format } from 'date-fns';
import ISLocale from 'date-fns/locale/is';
import USLocale from 'date-fns/locale/en-US';
import { useField, useFormikContext } from 'formik';
import DatePicker from "react-datepicker";

// Icons:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';

interface IProps {
    [x: string]: any,
    name: string,
    value?: string | null,
    label?: string,
    size?: string,
    lang?: string,
    maxDate?: Date,
    minDate?: Date,
    disabled?: boolean,
    id?: string,
    max_width?: boolean,
    dateFormat?: string,
    popperPlacement?: 'auto' | 'top' | 'bottom',
    showYearDropdown?: boolean,
    showMonthDropdown?: boolean,
    yearDropdownItemNumber?: number
}

export const DatePickerField = ({
    label = 'Dagsetning ',
    size = 'lg',
    lang = 'is',
    maxDate,
    minDate,
    dateFormat = 'dd.MM.yyyy',
    max_width,
    disabled = false,
    id = 'date-picker',
    popperPlacement = 'auto',
    showYearDropdown = false,
    showMonthDropdown = false,
    yearDropdownItemNumber = 100,
    ...props
}: IProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const locale = (lang === 'is') ? ISLocale : USLocale;
    // Label starts inline, but once date or isopen changes it goes on top.
    const { setFieldValue, submitCount, setFieldTouched } = useFormikContext();
    const [field, meta] = useField(props.name);
    const isLabelOnTop = ((field.value !== '' && field.value !== null && field.value !== undefined) || isOpen);
    // Define popper modifiers to ensure proper positioning
    const popperModifiers = [
        {
            name: "offset",
            options: {
                offset: [0, 0] // [skidding, distance] - 10px distance from reference element
            }
        },
        {
            name: "preventOverflow",
            options: {
                rootBoundary: 'viewport', // Use viewport as boundary, not the modal
                escapeWithReference: true, // Allow the popper to escape its reference element
                altBoundary: true, // Ignore ancestor boundaries (like modal)
                padding: 8 // Add some padding to avoid sticking to the edge
            }
        },
        {
            name: "flip",
            options: {
                // If popperPlacement is 'auto', allow flipping between top and bottom
                // If it's forced to 'top' or 'bottom', disable flipping
                fallbackPlacements: popperPlacement === 'auto' ? ['top', 'bottom'] : [],
                altBoundary: true // Ignore ancestor boundaries
            }
        },
        {
            name: "computeStyles",
            options: {
                gpuAcceleration: false, // Sometimes helps with positioning issues
                adaptive: false // Keeps the position fixed during scrolling
            }
        }
    ];

    // Add CSS to ensure the date picker can overflow modal
    // This will be added once on component mount
    useEffect(() => {
        // Check if the style already exists to avoid duplication
        if (!document.getElementById('datepicker-modal-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'datepicker-modal-styles';
            styleEl.innerHTML = `
                .datepicker-popper-override {
                    z-index: 1060 !important; /* Higher than modal's z-index */
                }
                .react-datepicker-popper {
                    position: fixed !important; /* Makes it relative to viewport */
                    z-index: 1060 !important;
                }
                .react-datepicker__month-select,
                .react-datepicker__year-select {
                    border: none !important;
                    background: transparent !important;
                    font-weight: bold !important;
                    cursor: pointer;
                    outline: none;
                }
                .react-datepicker__month-select:focus,
                .react-datepicker__year-select:focus {
                    outline: none;
                }
            `;
            document.head.appendChild(styleEl);
        }
    }, []);

    return <>
        <div>
            <div
                className={cx('KCL_datepicker', size, {
                    'is-open': isOpen,
                    'has-error': meta.error && meta.touched,
                    'disabled': disabled,
                    'max_width': max_width
                })}
            >
                <label
                    htmlFor={id}
                    className={cx("datepicker__label", {
                        'is-on-top': isLabelOnTop,
                        'disabled': disabled,
                        'has-error': meta.error && meta.touched
                    })}
                >
                    {label}
                </label>
                {disabled
                    ? <input
                        disabled
                        className={cx('datepicker__input', 'disabled', { 'max_width': max_width })}
                        id={id}
                    />
                    : <DatePicker
                        name={field.name}
                        onBlur={submitCount > 0 ? () => setFieldTouched(field.name, true) : undefined}
                        id={id}
                        className={cx('datepicker__input', { 'has-error': meta.error && meta.touched, 'max_width': max_width })}
                        selected={(field.value && new Date(field.value)) || null}
                        onChange={(val) => {
                            setFieldValue(field.name, val);
                        }}
                        dateFormat={dateFormat}
                        locale={locale}
                        selectsRange={false}
                        onCalendarOpen={() => setIsOpen(true)}
                        onCalendarClose={() => setIsOpen(false)}
                        minDate={minDate}
                        maxDate={maxDate}
                        // Enable year and month dropdowns for easier navigation
                        showYearDropdown={showYearDropdown}
                        showMonthDropdown={showMonthDropdown}
                        dropdownMode="select"
                        yearDropdownItemNumber={yearDropdownItemNumber}
                        // Configure popper placement - 'auto' lets it choose, or force 'top'/'bottom'
                        popperPlacement={popperPlacement === 'auto' ? 'bottom-start' : popperPlacement}
                        // Set popper props for higher z-index
                        popperProps={{
                            strategy: "fixed",
                            modifiers: popperModifiers
                        }}
                        // Add this if you want the calendar to open below even when there's not enough space
                        popperClassName="datepicker-popper-override"
                        autoComplete='off'
                        renderCustomHeader={({
                            monthDate,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                            changeYear,
                            changeMonth
                        }) => (
                            <div className="react-datepicker__header-inner">
                                <button
                                    aria-label={lang === 'is' ? 'Fyrri mánuður' : 'Previous Month'}
                                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        decreaseMonth()
                                    }}
                                    disabled={prevMonthButtonDisabled}
                                >
                                    <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.281 9.719a1.01 1.01 0 0 0 1.094.219C5.75 9.78 6 9.405 6 9V1c0-.375-.25-.75-.625-.906a1.01 1.01 0 0 0-1.094.218l-4 4A.97.97 0 0 0 0 5a.99.99 0 0 0 .281.719l4 4Z" fill="currentColor" />
                                    </svg>
                                </button>

                                <div className="react-datepicker__current-month">
                                    {showMonthDropdown && (
                                        <select
                                            value={monthDate.getMonth()}
                                            onChange={(e) => changeMonth(Number(e.target.value))}
                                            className="react-datepicker__month-select"
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>
                                                    {format(new Date(2000, i, 1), 'MMMM', { locale })}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {!showMonthDropdown && !showYearDropdown && format(monthDate, 'MMMM y', { locale: locale })}
                                    {showYearDropdown && (
                                        <select
                                            value={monthDate.getFullYear()}
                                            onChange={(e) => changeYear(Number(e.target.value))}
                                            className="react-datepicker__year-select"
                                        >
                                            {Array.from({ length: yearDropdownItemNumber }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    )}
                                </div>

                                <button
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        increaseMonth()
                                    }}
                                    disabled={nextMonthButtonDisabled}
                                    aria-label={lang === 'is' ? 'Næsti mánuður' : 'Next Month'}
                                    className="react-datepicker__navigation react-datepicker__navigation--next"
                                >
                                    <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1.688.313A1.01 1.01 0 0 0 .593.092.984.984 0 0 0 0 1v8c0 .406.219.781.594.938a1.01 1.01 0 0 0 1.094-.22l4-4C5.875 5.532 6 5.282 6 5c0-.25-.125-.5-.313-.688l-4-4Z" fill="currentColor" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    />
                }
                {(meta.error && meta.touched)
                    ? <FontAwesomeIcon
                        icon={faTimesCircle}
                        className="datepicker__icon icon-fa error-icon"
                    />
                    : <FontAwesomeIcon
                        icon={faCalendarDay}
                        className="datepicker__icon"
                    />
                }
            </div>
            {meta.error && meta.touched && <div className="datePicker-error-message ">{meta.error}</div>}
        </div>
    </>;
}